import React, { useState } from 'react';

const ImportantInfoTab = ({ person, updateField }) => {
  const importantInfoItems = person.importantInfoItems || [];
  const negativeItems = person.negativeItems || [];

  const [newItem, setNewItem] = useState({ title: '', content: '', shortLabel: '' });

  const handleAddImportant = () => {
    if (!newItem.title.trim() || !newItem.content.trim()) return;
    const item = {
      id: Date.now().toString(),
      title: newItem.title,
      content: newItem.content,
      shortLabel: newItem.shortLabel,
    };
    updateField('importantInfoItems', [...importantInfoItems, item]);
    setNewItem({ title: '', content: '', shortLabel: '' });
  };

  const handleAddNegative = () => {
    if (!newItem.title.trim() || !newItem.content.trim()) return;
    const item = {
      id: Date.now().toString(),
      title: newItem.title,
      content: newItem.content,
    };
    updateField('negativeItems', [...negativeItems, item]);
    setNewItem({ title: '', content: '', shortLabel: '' });
  };

  const handleRemoveImportant = (id) => {
    updateField('importantInfoItems', importantInfoItems.filter(item => item.id !== id));
  };

  const handleRemoveNegative = (id) => {
    updateField('negativeItems', negativeItems.filter(item => item.id !== id));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-4 p-4">
      {/* Add Important Info Card */}
      <div className="tech-card p-4">
        <h3 className="tech-section-title mb-4 flex items-center gap-2">
          <svg className="w-4 h-4 text-tech-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          ADD INFORMATION
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-mono text-tech-textMuted mb-2">TITLE</label>
              <input
                type="text"
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                placeholder="Enter title"
                className="tech-input"
              />
            </div>
            <div>
              <label className="block text-xs font-mono text-tech-textMuted mb-2">SHORT LABEL</label>
              <input
                type="text"
                value={newItem.shortLabel}
                onChange={(e) => setNewItem({ ...newItem, shortLabel: e.target.value })}
                placeholder="Short label"
                className="tech-input"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono text-tech-textMuted mb-2">CONTENT</label>
            <textarea
              value={newItem.content}
              onChange={(e) => setNewItem({ ...newItem, content: e.target.value })}
              rows="3"
              placeholder="Enter detailed information"
              className="tech-input resize-none"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleAddImportant}
              className="flex-1 py-2 px-4 bg-tech-success text-tech-bg font-medium text-sm rounded hover:bg-tech-success/90 transition"
            >
              ADD TO IMPORTANT
            </button>
            <button
              onClick={handleAddNegative}
              className="flex-1 py-2 px-4 bg-tech-danger text-white font-medium text-sm rounded hover:bg-tech-danger/90 transition"
            >
              ADD TO NEGATIVES
            </button>
          </div>
        </div>
      </div>

      {/* Important Information List */}
      <div className="tech-card p-4">
        <h3 className="tech-section-title mb-4 flex items-center gap-2">
          <svg className="w-4 h-4 text-tech-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          IMPORTANT INFORMATION
        </h3>
        {importantInfoItems.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-tech-textDim text-sm">NO IMPORTANT INFORMATION ADDED</p>
          </div>
        ) : (
          <div className="space-y-3">
            {importantInfoItems.map((item) => (
              <div key={item.id} className="bg-tech-surface rounded-lg p-4 border-l-4 border-tech-success">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-tech-text font-semibold">{item.title}</h4>
                      {item.shortLabel && (
                        <span className="px-2 py-0.5 bg-tech-success/20 text-tech-success text-xs rounded font-mono">
                          {item.shortLabel}
                        </span>
                      )}
                    </div>
                    <p className="text-tech-textMuted text-sm whitespace-pre-wrap">{item.content}</p>
                  </div>
                  <button
                    onClick={() => handleRemoveImportant(item.id)}
                    className="ml-4 p-2 text-tech-textMuted hover:text-tech-danger rounded transition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Negative Information List */}
      <div className="tech-card p-4">
        <h3 className="tech-section-title mb-4 flex items-center gap-2">
          <svg className="w-4 h-4 text-tech-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          NEGATIVE INFORMATION
        </h3>
        {negativeItems.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-tech-textDim text-sm">NO NEGATIVE INFORMATION ADDED</p>
          </div>
        ) : (
          <div className="space-y-3">
            {negativeItems.map((item) => (
              <div key={item.id} className="bg-tech-surface rounded-lg p-4 border-l-4 border-tech-danger">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-tech-text font-semibold mb-2">{item.title}</h4>
                    <p className="text-tech-textMuted text-sm whitespace-pre-wrap">{item.content}</p>
                  </div>
                  <button
                    onClick={() => handleRemoveNegative(item.id)}
                    className="ml-4 p-2 text-tech-textMuted hover:text-tech-danger rounded transition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* General Important Info Text Area */}
      <div className="tech-card p-4">
        <h3 className="tech-section-title mb-4">GENERAL NOTES</h3>
        <textarea
          value={person.importantInfo || ''}
          onChange={(e) => updateField('importantInfo', e.target.value)}
          rows="6"
          placeholder="Enter any additional important information here..."
          className="tech-input resize-none"
        />
      </div>
    </div>
  );
};

export default ImportantInfoTab;
